import { marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * BQ-18: Render a small subset of Markdown to sanitized HTML for use inside
 * label tooltips and the (read-only) preview within LabelEditor.
 *
 * Why both marked + DOMPurify:
 *  - `marked` does the Markdown → HTML conversion; we configure it with
 *    `breaks: true` so a single newline becomes a `<br>` (more useful for
 *    short tooltip strings than the standard "two-spaces or blank line"
 *    behavior).
 *  - `DOMPurify` strips anything our allowlist disallows. We deliberately
 *    refuse `<script>`, `<iframe>`, `<style>`, and any DOM event handler
 *    (`on*` attributes) — defense in depth even though label definitions
 *    come from the same JSON document the user already trusts.
 */
const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'em',
  'b',
  'i',
  'u',
  'code',
  'pre',
  'blockquote',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'a',
  'span',
];

const ALLOWED_ATTR = ['href', 'title', 'target', 'rel', 'class'];

// Configure marked once at module load. `breaks: true` is the only knob we
// flip; everything else is the default GFM-ish parser.
marked.setOptions({
  breaks: true,
  gfm: true,
});

export function renderMarkdown(input: string): string {
  if (!input) return '';
  // marked.parse() can return either string or Promise<string> depending on
  // configured options; with our sync settings it returns string. Guard with
  // a String() coercion so the type-checker stays happy without changing the
  // global async flag.
  const rawHtml = String(marked.parse(input));
  return DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Belt and braces: forbid event handlers explicitly even though they're
    // outside ALLOWED_ATTR.
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
    FORBID_TAGS: ['script', 'iframe', 'style', 'object', 'embed'],
  });
}
